import React, { useState, useEffect } from 'react';
import SharedHeaderHomeowner from './SharedHeaderHomeowner';
// CB:BEGIN inline-help-shortcut-import
import InlineHelpShortcut from './help/InlineHelpShortcut';
// CB:END inline-help-shortcut-import
import { useUser } from '@clerk/clerk-react';
import { useProgressiveDataService, getTempSessionId } from '../lib/progressiveDataService';
import { eventTracker } from './DataTracking';
import { useDevice } from '../hooks/useDevice';

interface ProjectWizardProps {
  onNavigate: (screen: string) => void;
}

const ProjectWizard: React.FC<ProjectWizardProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const { getHomeowner, getTempHomeowner, createOrUpdateTempHomeowner, ensureActiveProject } = useProgressiveDataService();
  const [estimateData, setEstimateData] = useState<any>(null);
  const [address, setAddress] = useState<string>('');
  const [parsedAddress, setParsedAddress] = useState<any>(null);
  const { isMobileLayout } = useDevice();

  useEffect(() => {
    // Get estimate data from localStorage if available
    const savedEstimate = localStorage.getItem('quickEstimate');
    const savedAddress = localStorage.getItem('enteredAddress');
    
    if (savedEstimate) {
      try {
        const parsedEstimate = JSON.parse(savedEstimate);
        console.log('📊 Loaded estimate data from localStorage:', parsedEstimate);
        setEstimateData(parsedEstimate);
      } catch (error) {
        console.error('Error parsing saved estimate:', error);
      }
    }
    
    if (savedAddress) {
      setAddress(savedAddress);
      // Parse the address to extract components for display
      try {
        // Handle full address format like "123 Rosewood Way, O'Fallon, MO 63366, USA"
        const parts = savedAddress.split(',').map(p => p.trim());
        const street = parts[0] || '';
        
        let state = '';
        let zipCode = '';
        let city = '';
        
        // Find zip code first (5 digits)
        const zipMatch = savedAddress.match(/\b(\d{5})\b/);
        if (zipMatch) {
          zipCode = zipMatch[1];
        }
        
        // Find state (2 letters) - look for pattern like "MO 63366" or just "MO"
        const stateMatch = savedAddress.match(/\b([A-Z]{2})\s+(\d{5})\b/);
        if (stateMatch) {
          state = stateMatch[1];
          // City is the part before the state
          const stateIndex = parts.findIndex(part => part.includes(state));
          if (stateIndex > 0) {
            city = parts[stateIndex - 1];
          }
        } else {
          // Fallback: look for just state
          const stateOnlyMatch = savedAddress.match(/\b([A-Z]{2})\b/);
          if (stateOnlyMatch) {
            state = stateOnlyMatch[1];
            const stateIndex = parts.findIndex(part => part.includes(state));
            if (stateIndex > 0) {
              city = parts[stateIndex - 1];
            }
          }
        }
        
        console.log('📍 Parsed address components:', { street, city, state, zipCode, fullAddress: savedAddress });
        setParsedAddress({ street, city, state, zipCode, fullAddress: savedAddress });
      } catch (error) {
        console.error('Error parsing address:', error);
        setParsedAddress({ street: savedAddress, city: '', state: '', zipCode: '', fullAddress: savedAddress });
      }
    }
  }, []);

  const handleProjectTypeSelect = async (projectType: 'ROOF' | 'SIDING' | 'GUTTERS' | 'WINDOWS' | 'DOORS' | 'OTHER') => {
    try {
      console.log('🔄 Creating project with type:', projectType);
      try {
        localStorage.setItem('pendingProjectType', projectType);
      } catch {}
      
      if (user) {
        // User is signed in - get homeowner ID and create project
        try {
          const homeowner = await getHomeowner(user.id);
          // Only create project here if we already have a valid full address from localStorage
          const savedAddress = (localStorage.getItem('enteredAddress') || '').trim();
          const isFullAddress = savedAddress.includes(',') && /\d{5}/.test(savedAddress);
          if (isFullAddress) {
            await ensureActiveProject({
              homeownerId: homeowner.id,
              address: savedAddress,
              projectType,
              logLabel: '[ProjectWizard]',
            });
            console.log('✅ Ensured project for signed-in homeowner with full address');
          } else {
            console.log('⏭️ Skipping project creation until full address is available');
          }
        } catch (error) {
          console.error('❌ Error creating project for homeowner:', error);
          // Continue with navigation even if project creation fails
        }
      } else {
        // User is not signed in - get temp homeowner ID and create project
        try {
          const tempSessionId = getTempSessionId();
          // CB:BEGIN ensure-temp-homeowner-exists
          try {
            const savedAddress = localStorage.getItem('enteredAddress') || '';
            const savedEstimate = localStorage.getItem('quickEstimate') || '';
            await createOrUpdateTempHomeowner({ 
              temp_session_id: tempSessionId, 
              address: savedAddress,
              quick_estimate: savedEstimate
            });
          } catch (_) {}
          const tempHomeowner = await getTempHomeowner(tempSessionId);
          // CB:END ensure-temp-homeowner-exists
          const savedAddress = (localStorage.getItem('enteredAddress') || '').trim();
          const isFullAddress = savedAddress.includes(',') && /\d{5}/.test(savedAddress);
          if (isFullAddress) {
            await ensureActiveProject({
              tempHomeownerId: tempHomeowner.id,
              tempSessionId,
              address: savedAddress,
              projectType,
              logLabel: '[ProjectWizard]',
            });
            console.log('✅ Ensured project for temp homeowner with full address');
          } else {
            console.log('⏭️ Skipping project creation for temp homeowner until full address is available');
          }
        } catch (error) {
          console.error('❌ Error creating project for temp homeowner:', error);
          // Continue with navigation even if project creation fails
        }
      }
      
      // Navigate to estimate form
      onNavigate('estimate-form');
    } catch (error) {
      console.error('❌ Error in project type selection:', error);
      // Still navigate to estimate form even if there's an error
      onNavigate('estimate-form');
    }
  };

  return (
    <div className="container">
      <SharedHeaderHomeowner onNavigate={onNavigate} />
      
      {/* CB:BEGIN inline-help-shortcut-create-project */}
      <div style={{ maxWidth: '900px', margin: '14px auto 0 auto' }}>
        <InlineHelpShortcut
          onNavigate={onNavigate}
          sectionId="creating-a-project"
          label="Need help creating your project?"
        />
      </div>
      {/* CB:END inline-help-shortcut-create-project */}


      {/* Quick Estimate Section */}
      {estimateData && address && (
        <div style={{ 
          maxWidth: '900px', 
          margin: '30px auto', 
          padding: '30px',
          background: 'white',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          color: '#1f2937'
        }}>
          <h2 style={{
            fontSize: 'clamp(24px, 5.25vw, 30px)',
            fontWeight: '700',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            textAlign: 'center',
            color: '#1f2937'
          }}>
            Quick Estimate for {(() => {
              // CB:BEGIN address-display-fix
              // Build display address from parsed components
              if (parsedAddress?.street && parsedAddress?.city && parsedAddress?.state && parsedAddress?.zipCode) {
                // Use parsed components: "123 Rosewood Way, O'Fallon, MO 63366"
                return `${parsedAddress.street}, ${parsedAddress.city}, ${parsedAddress.state} ${parsedAddress.zipCode}`;
              } else if (parsedAddress?.fullAddress) {
                // Fallback to full address, strip ", USA" if present
                return parsedAddress.fullAddress.replace(/,\s*USA\s*$/i, '');
              } else {
                // Last resort: use the raw address
                return address.replace(/,\s*USA\s*$/i, '');
              }
              // CB:END address-display-fix
            })()}
          </h2>
          <div style={{
            fontSize: 'clamp(48px, 8.5vw, 72px)',
            fontWeight: '900',
            color: '#2563eb',
            marginBottom: '18px',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: '1.2',
            maxWidth: '100%',
            overflow: 'hidden',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: 'clamp(42px, 7.5vw, 66px)',
              lineHeight: '1.1',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}>
              {estimateData.displayPrice}
            </div>
          </div>
          <p style={{ 
            color: '#6b7280', 
            marginBottom: '0',
            fontSize: 'clamp(19.5px, 4.2vw, 22.5px)',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: '1.4',
            textAlign: 'center'
          }}>
            {(() => {
              const surfaceArea = estimateData.details?.surfaceAreaSqft?.toLocaleString() || 'N/A';
              const pitch = estimateData.details?.pitchDeg || 'N/A';
              const confidence = Math.round((estimateData.details?.confidence || 0) * 100);
              console.log('📊 Estimate display values:', { surfaceArea, pitch, confidence, details: estimateData.details });
              return `Based on your Home's Sqft, ${pitch}° pitch, ${confidence}% confidence`;
            })()}
          </p>
        </div>
      )}

      <div className="content" style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
        {/* Continue to Detailed Estimate Button */}
        <div 
          className="continue-button"
          style={{ 
            padding: isMobileLayout ? '16px 20px' : '30px 60px',
            textAlign: 'center',
            cursor: 'pointer',
            border: '2px solid #2563eb',
            borderRadius: '8px',
            backgroundColor: '#2563eb',
            color: '#ffffff',
            transition: 'all 0.2s ease',
            marginBottom: isMobileLayout ? '16px' : '30px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1d4ed8';
            e.currentTarget.style.borderColor = '#1d4ed8';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(37, 99, 235, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2563eb';
            e.currentTarget.style.borderColor = '#2563eb';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
          onClick={() => {
            eventTracker.track('estimate_form_entered');
            handleProjectTypeSelect('ROOF');
          }}
        >
          <h4 style={{ fontSize: isMobileLayout ? '18px' : '36px', fontWeight: 600, margin: 0 }}>Continue to Real Pro Estimates!</h4>
        </div>
      </div>
    </div>
  );
};

export default ProjectWizard; 